import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { BrapiSyncContextService } from '../brapi-sync-context.service';
import { HttpClient, HttpContext } from '@angular/common/http';
import { brapiAll, handleStatusMessages, toText } from '../util/brapi-all';
import { EntityEnum, ExternalReferenceService } from '../shared/external-reference/external-reference.service';
import { ObservationLevel } from '../shared/brapi/shared/model/observation-level';
import { AlertService } from '../../shared/alert/alert.service';
import { BrapiSyncPath, BrapiSyncService } from '../brapi-sync.service';
import { EnablePageBlocking, EnableSavingIndicator, LoadingService } from '../../shared/loading-indicator';
import { TranslateService } from '@ngx-translate/core';

declare const BrAPI: any;

@Component({
    selector: 'app-observation-unit',
    templateUrl: './observation-unit.component.html',
    styleUrls: ['./observation-unit.component.css']
})
export class ObservationUnitComponent implements OnInit {

    @ViewChild('downloadError') downloadErrorTemplate: TemplateRef<any>;

    brapiSource: any;
    brapiDestination: any;
    sourceGermplasm: any[] = [];
    sourceObservationUnits: any[] = [];
    page = 1;
    pageSize = 20;
    germplasmTotalCount = 0;
    germplasmLoading = false;
    observationUnitLoading = false;
    isSaving = false;
    observationUnitsAlreadyExist = false;
    observationUnitsSaved = false;
    germplasmByGermplasmDbId: any = {};
    germplasmInDestinationByPUIs: any = {};
    germplasmInDestinationByRefIds: any = {};

    errors: any = [];
    info: any = [];

    constructor(private router: Router,
                private http: HttpClient,
                public externalReferenceService: ExternalReferenceService,
                public context: BrapiSyncContextService,
                private alertService: AlertService,
                private brapiSyncService: BrapiSyncService,
                private loadingService: LoadingService,
                private translateService: TranslateService) {
        this.brapiSource = BrAPI(this.context.source, '2.0', this.context.sourceToken);
        this.brapiDestination = BrAPI(this.context.destination, '2.0', this.context.destinationToken);
    }

    ngOnInit(): void {
        this.alertService.closeAllAlerts();
        // Get the germplasm of the study from source
        this.loadGermplasm();
        // Get the observation units of the study from source
        this.loadObservationUnits();
        this.checkObservationAlreadyExists();
    }

    cancel(): void {
        this.brapiSyncService.navigateToPage(BrapiSyncPath.EntitySelector);
    }

    async post(): Promise<void> {
        this.isSaving = true;
        // Load all germplasm from source
        const res: any = await this.http.get(this.context.source + '/germplasm', {
            params: {
                studyDbId: this.context.sourceStudy.studyDbId,
                pageSize: '10000'
            }
        }).toPromise();
        const allGermplasm = res.result.data;

        await this.searchInTarget(allGermplasm);

        let data: any;
        try {
            data = this.transform(this.sourceObservationUnits);
        } catch (message) {
            this.alertService.error('error.custom', { param: message });
            this.isSaving = false;
            return;
        }
        try {

            const postRes: any = await this.http.post(this.context.destination + '/observationunits', data, { context: new HttpContext().set(EnableSavingIndicator, true).set(EnablePageBlocking, true) }
            ).toPromise();

            this.errors = postRes.metadata.status?.filter((s: any) => s.messageType === 'ERROR');
            this.info = postRes.metadata.status?.filter((s: any) => s.messageType === 'INFO');

            if (this.errors.length) {
                this.alertService.errorAsTemplate(this.downloadErrorTemplate, 30000);
            } else {
                this.alertService.success('brapi-sync.observation-units.success.import');
            }

            this.observationUnitsSaved = this.errors.length === 0;

        } catch (error: any) {
            this.observationUnitsSaved = false;
        }
        this.isSaving = false;

    }

    loadObservationUnits(): void {
        this.observationUnitLoading = true;
        // Only load the observation units for PLOT level observation units
        this.brapiSource.search_observationunits({
            studyDbIds: [this.context.sourceStudy.studyDbId],
            // Filter the observation units by PLOT level
            observationLevels: [{
                levelName: ObservationLevel.PLOT
            }],
            // Workaround for filtering PLOT observation units for older version of BMS
            observationLevel: ObservationLevel.PLOT
        }
        ).all((result: any) => {
            this.sourceObservationUnits = result;
            this.observationUnitLoading = false;
        });
    }

    async loadGermplasm(): Promise<void> {
        this.germplasmLoading = true;
        try {
            const res: any = await this.http.get(this.context.source + '/germplasm', {
                params: {
                    studyDbId: this.context.sourceStudy.studyDbId,
                    page: (this.page - 1).toString(),
                    pageSize: this.pageSize.toString(),
                }
            }).toPromise();
            this.sourceGermplasm = res.result.data;
            this.germplasmTotalCount = res.metadata.pagination.totalCount;

            if (!this.sourceGermplasm || this.sourceGermplasm.length === 0) {
                this.alertService.error('brapi-sync.germplasm-source-modal.no-germplasm-records-found');
            } else {
                // Check if the germplasm already exists in the destination server.
                await this.searchInTarget(this.sourceGermplasm);
            }

        } catch (error) {
            this.alertService.error('error.custon', { param:error });
        }
        this.germplasmLoading = false;
    }

    async searchInTarget(germplasm: any[]): Promise<void> {

        if (germplasm && germplasm.length) {
            germplasm.forEach((g: any) => {
                this.germplasmByGermplasmDbId[g.germplasmDbId] = g;
            });
        }

        // Find germplasm in destination by Permanent Unique Identifier (germplasmPUI)
        const germplasmPUIs = germplasm.filter(g => g.germplasmPUI !== null && g.germplasmPUI !== undefined).map(g => g.germplasmPUI);
        let currentPage = 0;
        let totalPages = 1;
        // FIXME: This is a workaround to get all the items in all pages.
        // Brapi-Js doesn't have a way to specify the page size, so a brapi call will always only return
        // 1000 records from the first page.
        if (germplasmPUIs.length) {
            while (currentPage <= totalPages) {
                const germplasmByPUIsResult = await brapiAll(this.brapiDestination.search_germplasm({
                    germplasmPUIs,
                    pageRange: [currentPage, 1]
                }));
                if (germplasmByPUIsResult && germplasmByPUIsResult.length) {
                    const tempCurrentPage = germplasmByPUIsResult[0].__response.metadata.pagination.currentPage;
                    currentPage = tempCurrentPage ? (tempCurrentPage + 1) : 1;
                    totalPages = germplasmByPUIsResult[0].__response.metadata.pagination.totalPages - 1;
                    if (germplasmByPUIsResult[0].data.length) {
                        germplasmByPUIsResult[0].data.forEach((g: any) => {
                            this.germplasmInDestinationByPUIs[g.germplasmPUI] = g;
                        });
                    }
                }
            }
        }

        // Find germplasm in destination by external reference ID
        const germplasmRefIds = germplasm.map(g => this.externalReferenceService.getReferenceId(EntityEnum.GERMPLASM, g.germplasmDbId));
        currentPage = 0;
        totalPages = 1;
        // FIXME: This is a workaround to get all the items in all pages.
        // Brapi-Js doesn't have a way to specify the page size, so a brapi call will always only return
        // 1000 records from the first page.
        if (germplasmRefIds.length) {
            while (currentPage <= totalPages) {
                const germplasmByRefIdsResult = await brapiAll(this.brapiDestination.search_germplasm({
                    externalReferenceIDs: germplasmRefIds,
                    pageRange: [currentPage, 1]
                }));
                if (germplasmByRefIdsResult && germplasmByRefIdsResult.length) {
                    const tempCurrentPage = germplasmByRefIdsResult[0].__response.metadata.pagination.currentPage;
                    currentPage = tempCurrentPage ? (tempCurrentPage + 1) : 1;
                    totalPages = germplasmByRefIdsResult[0].__response.metadata.pagination.totalPages - 1;
                    if (germplasmByRefIdsResult[0].data.length) {
                        germplasmByRefIdsResult[0].data.forEach((g: any) => {
                            if (g.externalReferences && g.externalReferences.length) {
                                g.externalReferences.forEach((ref: any) => {
                                    this.germplasmInDestinationByRefIds[ref.referenceID] = g;
                                });
                            }
                        });
                    }
                }
            }
        }
    }

    getTargetGermplasm(germplasm: any): any {
        if (this.germplasmInDestinationByPUIs[germplasm.germplasmPUI]) {
            return this.germplasmInDestinationByPUIs[germplasm.germplasmPUI];
        } else {
            const referenceId = this.externalReferenceService.getReferenceId(EntityEnum.GERMPLASM, germplasm.germplasmDbId);
            return this.germplasmInDestinationByRefIds[referenceId];
        }
    }

    transform(observationUnits: any[]): any {
        return observationUnits.map(observationUnit => {

            const targetGermplasm = this.getTargetGermplasm(this.germplasmByGermplasmDbId[observationUnit.germplasmDbId]);
            if (!targetGermplasm) {
                throw new Error(this.translateService.instant('brapi-sync.observation-unit.error.germplasm-not-imported'));
            }
            return {
                additionalInfo: observationUnit.additionalInfo, // TODO: IBP-4725 Fix search observationunit schema first
                externalReferences: this.externalReferenceService.generateExternalReference(observationUnit.observationUnitDbId, EntityEnum.OBSERVATIONUNITS,
                    observationUnit.externalReferences),
                germplasmDbId: targetGermplasm.germplasmDbId,
                germplasmName: targetGermplasm.germplasmName,
                locationDbId: this.context.targetLocation.locationDbId,
                locationName: this.context.targetLocation.locationName,
                observationUnitName: observationUnit.observationUnitName,
                observationUnitPUI: observationUnit.observationUnitPUI,
                observationUnitPosition: observationUnit.observationUnitPosition, // TODO: IBP-4725 Fix search observationunit schema first
                programDbId: this.context.targetProgram.programDbId,
                programName: this.context.targetProgram.programName,
                seedLotDbId: observationUnit.seedLotDbId,
                studyDbId: this.context.targetStudy.studyDbId,
                studyName: this.context.targetStudy.studyName,
                treatments: observationUnit.treatments,
                trialDbId: this.context.targetTrial.trialDbId,
                trialName: this.context.targetTrial.trialName
            };
        });
    }

    checkObservationAlreadyExists(): void {
        // Only load the observation units for PLOTs
        this.brapiDestination.search_observationunits({
            studyDbIds: [this.context.targetStudy.studyDbId],
            observationLevels: [{
                levelName: ObservationLevel.PLOT
            }],
            pageRange: [0, 1],
            pageSize: 1
        }
        ).all((result: any) => {
            if (result.length) {
                this.observationUnitsAlreadyExist = true;
                this.alertService.warning('brapi-sync.observation-unit.warning.already-exists');
            }
        });
    }

    isValid(): boolean {
        return !this.germplasmLoading && !this.observationUnitLoading && !this.isSaving && !this.observationUnitsAlreadyExist && !this.observationUnitsSaved
            && this.sourceObservationUnits.length > 0 && this.sourceGermplasm.length > 0;
    }

    async next(): Promise<void> {
        this.brapiSyncService.navigateToPage(BrapiSyncPath.Variable);
    }

    canProceed(): boolean {
        return this.observationUnitsAlreadyExist || this.observationUnitsSaved;
    }

    downloadErrorsAndInfo(): void {
        const lines: string[] = [];

        if (this.info && this.info.length) {
            lines.push('=== INFO ===');
            this.info.forEach((i: any) => {
                lines.push(`[INFO] ${i.message || JSON.stringify(i)}`);
            });
        }

        if (this.errors && this.errors.length) {
            lines.push('=== ERRORS ===');
            this.errors.forEach((e: any) => {
                lines.push(`[ERROR] ${e.message || JSON.stringify(e)}`);
            });
            lines.push('');
        }

        const content = lines.join('\n');
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = 'errors-info-log.txt';
        a.click();

        URL.revokeObjectURL(url);
    }

}
